import {
    getFeatureInfoAtPosition,
    getFeatureInfoForBBox,
    getFeatureInfoForPolygon,
    shouldGetFeatureInfo,
    getFeaturesForGeometrySelection,
    getFeatureInfoForViewPort,
    calculateWorldDistance,
    layerFeaturesCanBePaged
} from '../../../services/map/feature/selection';
import i18n from '../../../i18n';
import { NEDGLOBE_VIEWER } from '../../../constants';

export const actions = {
    async getLayersToSelectFrom(store) {
        return store.state.defaultMap.layers
            .filter(({ visible }) => visible)
            .map(({ id }) => store.getters.getLayerById(id));
    },
    async getFeaturesFromBBox(store, { layerId, bbox, forceLoading, cancellationToken }) {
        const mapObject = store.getters.getMainMap;
        const map = mapObject.map;
        const layerData = store.getters.getLayerById(layerId);
        const maxFeatures = store.state.userData.maxFeatures;
        const features = await getFeatureInfoForBBox(store, map, layerData, bbox, maxFeatures, forceLoading, cancellationToken);
        return store.dispatch('filterWebMeasurementsNotFromUserSession', { layer: layerData, features });
    },
    async getFeaturesAtPosition(store, { position, resolution, layers }) {
        // Stop action if we are digitizing or measuring
        if (store.getters.stateDigiFeedback || store.getters.stateEditingFeedback) {
            return undefined;
        }
        const mapObject = store.getters.getMainMap;
        const map = mapObject.map;

        const isWeb = store.state.context === 'web';
        const webShowTableView = !!store.getters.getWebTheme?.data?.showTableView; // Double negation to cast undefined to false
        const showTableViewSmallDevices = store.getters.getShowTableViewSmallDevices;

        const layersToSearch = layers || store.getters.getActivatedLayers
            .filter(layerData => shouldGetFeatureInfo(layerData.extended, mapObject.viewport.resolution, isWeb, webShowTableView, showTableViewSmallDevices));

        const featurePromisses = layersToSearch
            .map(async layer => {
                const shiftedPosition = position.slice();
                if (layer.data.symbolShiftX && !resolution) {
                    shiftedPosition[0] -= calculateWorldDistance(map, layer.data.symbolShiftX / 1000.0);
                }
                if (layer.data.symbolShiftY && !resolution) {
                    shiftedPosition[1] -= calculateWorldDistance(map, layer.data.symbolShiftY / 1000.0);
                }
                const features = await getFeatureInfoAtPosition(store, map, layer, shiftedPosition, resolution);
                return store.dispatch('filterWebMeasurementsNotFromUserSession', { layer, features });
            });

        const featureResults = await Promise.allSettled(featurePromisses);
        return layersToSearch
            .map((layer, index) => {
                if (featureResults[index].status === 'rejected') {
                    store.dispatch('setLayerErrorState', { layerId: layer.id, error: { message: 'layer not found' } });
                    return null;
                }
                let meta = { type: layer.type };
                if (layer.type === 'theme') {
                    const theme = store.getters.getThemeById(layer.data.themeId);
                    meta = {
                        type: layer.type,
                        theme
                    };
                }
                if (layer.type === 'feedback') {
                    const feedback = store.getters.getFeedbackById(layer.data.feedbackId);
                    meta = {
                        type: layer.type,
                        feedback
                    };
                }
                return {
                    layer,
                    features: featureResults[index].value,
                    meta,
                    limitedResults: false
                };

            })
            .filter(item => item);
    },
    async getSingleLayerSelection(store, { layerId }) {
        const layerData = store.getters.getLayerById(layerId);
        const mapObject = store.getters.getMainMap;
        const map = mapObject.map;
        const maxFeatures = store.state.userData.maxFeatures;
        try {
            const features = await getFeatureInfoForViewPort(store, map, layerData, maxFeatures);
            return [{
                layer: layerData,
                features: features,
                meta: {},
                limitedResults: !layerFeaturesCanBePaged(layerData) && features.length > maxFeatures
            }];
        } catch (err) {
            store.dispatch('setLayerErrorState', { layerId: layerData.id, error: { message: 'layer not found' } });
            throw err;
        }
    },
    async getLayerSelection(store, { bbox, geometry, layers, within, force, checkLayerAttributes }) {
        // use only layers that have correct properties and are in view
        const mapObject = store.getters.getMainMap;
        const map = mapObject.map;

        const isWeb = store.state.context === 'web';
        const webShowTableView = !!store.getters.getWebTheme?.data?.showTableView; // Double negation to cast undefined to false
        const showTableViewSmallDevices = store.getters.getShowTableViewSmallDevices;

        const layersToSearch = layers || store.getters.getActivatedLayers
            .filter(layerData => shouldGetFeatureInfo(layerData.extended, mapObject.viewport.resolution, isWeb, webShowTableView, showTableViewSmallDevices, checkLayerAttributes));

        // stop executing is there are no layers to show results for
        if (layersToSearch.length === 0) {
            return [];
        }

        let featurePromisses;
        if (geometry) {
            // get the features from the geometry
            featurePromisses = layersToSearch
                // create requests
                .map(layerData => {
                    return getFeatureInfoForPolygon(store, map, layerData, geometry, store.state.userData.maxFeatures, force);
                });
        } else {
            // get the features from the bounding box
            featurePromisses = layersToSearch
                // create requests
                .map(layerData => {
                    return getFeatureInfoForBBox(store, map, layerData, bbox, store.state.userData.maxFeatures, force);
                });
        }
        // wait for all results to return
        const featureResults = await Promise.all(featurePromisses);

        // filter items with or without geometry overlap, if applicable
        const features = featureResults
            .map(result => {
                return geometry ? getFeaturesForGeometrySelection(result, geometry, within) : result;
            });

        // return a result set for selections
        return layersToSearch.map((layer, index) => {
            let meta = { type: layer.type };
            if (layer.type === 'theme') {
                const theme = store.getters.getThemeById(layer.data.themeId);
                meta = {
                    type: layer.type,
                    theme
                };
            }
            if (layer.type === 'feedback') {
                const feedback = store.getters.getFeedbackById(layer.data.feedbackId);
                meta = {
                    type: layer.type,
                    feedback
                };
            }
            return {
                layer,
                features: features[index],
                meta,
                limitedResults: !layerFeaturesCanBePaged(layer) && featureResults[index].length > store.state.userData.maxFeatures
            };
        });
    },
    async createAndDisplaySelection(store, { actionName, actionSettings, type, resetPointer = true, addSelectionToMap = false }) {
        store.commit('CLEAR_SELECTIONS');
        store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');
        const results = await store.dispatch(actionName, actionSettings);
        store.commit('ADD_SELECTION', { location: {}, type, results, refresh: { actionName, actionSettings, type } });
        if (resetPointer) {
            store.dispatch('updateSelectionPointer', {});
        }
        addSelectionToMap && store.dispatch('displaySelection', { groupIndex: 0 });
        store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
    },
    updateSelectionPointer(store, { group, index, showSelection = true }) {
        const firstFocussed = store.getters.getSelectionGroups.findIndex(item => item.meta?.focus);
        const preselectedGroup = firstFocussed > 0 ? firstFocussed : 0;
        const groupIndex = typeof group === 'number' ? group : preselectedGroup;
        const itemIndex = typeof index === 'number' ? index : -1;
        store.commit('RESET_SELECTIONS_FOCUS');
        store.commit('SET_SELECTION_POINTER', { group: groupIndex, index: itemIndex });
        showSelection && store.dispatch('displaySelectedItem', {});
    },
    displaySelectionFeaturesFromGroup(store, { groupIndex }) {
        const customLayerName = NEDGLOBE_VIEWER.customLayers.printable.selections;
        const currentGroup = store.getters.getSelectionGroups[typeof groupIndex === 'number' ? groupIndex : store.state.selectedGroupIndex];
        if (!currentGroup) {
            store.commit('RESET_MAP_FEATURES', { mapName: null, customLayerName });
            return;
        }
        const { computedStyles } = currentGroup.layer.extended;
        const features = currentGroup.features;
        store.commit('SHOW_MAP_FEATURE', {
            mapName: null,
            customLayerName,
            features,
            computedStyles,
            styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selection,
            reset: true
        });
    },
    displaySelectedItem(store, { groupIndex, itemIndex, showSelection = true }) {
        showSelection && store.dispatch('displaySelectionFeaturesFromGroup', { groupIndex });
        const customLayerName = NEDGLOBE_VIEWER.customLayers.printable.selection;
        const currentGroup = store.getters.getSelectionGroups[typeof groupIndex === 'number' ? groupIndex : store.state.selectedGroupIndex];
        if (!currentGroup) {
            store.commit('RESET_MAP_FEATURES', { mapName: null, customLayerName });
            return;
        }
        const feature = currentGroup.features[typeof itemIndex === 'number' ? itemIndex : store.state.selectedItemIndex];
        if (!feature) {
            store.commit('RESET_MAP_FEATURES', { mapName: null, customLayerName });
            return;
        }
        const { computedStyles } = currentGroup.layer.extended;
        store.commit('SHOW_MAP_FEATURE', {
            mapName: null,
            customLayerName,
            feature,
            computedStyles,
            styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
            reset: true
        });
    },
    async refreshSelection(store) {
        const refresh = store.getters.getSelectionData?.refresh;
        if (refresh) {
            store.dispatch('createAndDisplaySelection', { actionName: refresh.actionName, actionSettings: refresh.actionSettings, type: refresh.type });
        }
    },
    displaySelectedItemObjectData(store, { groupTitle, feature }) {
        const currentGroup = store.state.selectionsObjectData.find(({ title }) => title === groupTitle);
        if (currentGroup) {
            store.commit('SHOW_MAP_FEATURE', {
                mapName: null,
                customLayerName: NEDGLOBE_VIEWER.customLayers.printable.selections,
                features: currentGroup.results.features,
                computedStyles: currentGroup.results.computedStyles,
                styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selection,
                reset: true
            });
            if (feature) {
                store.commit('SHOW_MAP_FEATURE', {
                    mapName: null,
                    customLayerName: NEDGLOBE_VIEWER.customLayers.printable.selection,
                    feature,
                    computedStyles: currentGroup.results.computedStyles,
                    styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
                    reset: true
                });
            } else {
                store.commit('RESET_MAP_FEATURES', { mapName: null, customLayerName: NEDGLOBE_VIEWER.customLayers.printable.selection });
            }
        }
    },
    filterWebMeasurementsNotFromUserSession({ state, getters }, { layer, features }) {
        if (state.context === 'web' && layer.type === 'feedback' && getters.getWebThemeHasMetingenType) {
            return features.filter(feature => feature.getProperties()?.aanmaker === getters.getUserInfo.sessionId);
        }
        return features;
    },
    removeTabGroup(store, { title }) {
        if (title) {
            store.commit('CLEAR_SELECTION_DATA', { title });
        }
    },
    async prepareAddressDataAtPoint({ commit, dispatch }, { point, address }) {
        // Reset all stored data
        const addressAtPoint = {
            point: point,
            addressData: null,
            geocodeAddress: null,
            geocodeParcel: null,
            address: null,
            parcelGeometry: null,
            parcelIdentificationLocalID: null,
            parcelTitle: null
        };

        let addressInfo = await dispatch('getOrFormatAddress', { point: point, address: address });

        // Check if real address was found
        if (addressInfo) {
            if (addressInfo.streetName) {
                addressAtPoint.geocodeAddress = `${addressInfo.displayName}`;
                addressAtPoint.addressData = addressInfo;
            }

            // Store the parcelnumber of the closest address to the clicked location.
            if (addressInfo.gekoppeldPerceel) {
                addressAtPoint.geocodeParcel = addressInfo.gekoppeldPerceel;
            }
        }

        // Get parcel information at this point
        const result = await dispatch('getData', {
            url: 'brk/getParcelWfsInfo',
            params: { location: `${point[0]}, ${point[1]}` }
        });

        if (result?.data?.features &&
            result?.data?.features.length > 0
        ) {
            // determine parcel details
            const feature = result.data.features[0];
            // Store ID and Title
            addressAtPoint.parcelIdentificationLocalID = feature.properties.identificatieLokaalID;
            addressAtPoint.parcelTitle = `${feature.properties.akrKadastraleGemeenteCodeWaarde}-${feature.properties.sectie}-${feature.properties.perceelnummer}`;

            if (feature.geometry.coordinates.length > 0) {
                // Dispatch coordinates
                addressAtPoint.parcelGeometry = feature.geometry;
            }

            // Save kadastralegemeentecode
            addressAtPoint.kadastraleGemeenteCode = feature.properties.kadastraleGemeenteCode;

            // When geocode service returns a different parcelnumber than the BRK service
            // we are not on an address
            // First check if we have a geocodeParcel at all
            if (!addressAtPoint?.geocodeParcel?.includes(addressAtPoint.parcelTitle)) {
                addressAtPoint.address = i18n.t('brk.noAddressFound');
                addressAtPoint.addressData = null;
            } else {
                addressAtPoint.address = addressAtPoint.geocodeAddress.replace(', ', '\n');
            }
        }
        commit('SET_ADDRESSATPOINT', addressAtPoint);
    },
    async getOrFormatAddress({ dispatch }, { point, address }) {
        // if we have an incoming address that's because that is the result of searching for an address in the search bar.
        // No need to get an address from the location service because we already have one..
        if (address) {
            // Address from searchbar
            if (address.weergavenaam) {
                let data = {};
                const splitAddress = address.weergavenaam.split(' ');
                data.city = splitAddress[splitAddress.length - 1];
                data.gekoppeldPerceel = address.gekoppeld_perceel;
                data.huisNummer = address.huisnummer;
                data.huisNummerToevoeging = address.huisnummertoevoeging;
                data.huisLetter = address.huisletter;
                data.postCode = address.postcode;
                data.streetName = address.straatnaam;
                data.displayName = address.weergavenaam;
                return data;
            } else {
                // address from location service
                return address;
            }

        } else {
            // Get address-info for this location (call web-api)
            return dispatch('getAddressInfoV1', point);
        }
    }
};
